import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  MapPin,
  Calendar,
  BookOpen,
  Link2,
  Quote,
  ChevronRight,
  Star,
  Bookmark,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { Section, Badge, AnimatedCard } from "@/components/ui/shared";
import { useBookmarks } from "@/lib/bookmarks";
import { useToast } from "@/components/ui/Toast";
import ReadingProgress from "@/components/ui/ReadingProgress";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import { useSEO } from "@/hooks/useSEO";
import { articleJsonLd, snippet } from "@/lib/seo";
import JsonLd from "@/components/ui/JsonLd";
import { SkeletonDetailPage } from "@/components/ui/Skeleton";
import { errorMessage, isNotFound, useSeerahEvent, usePageView } from "@/lib/api";
import type { SourceResponse, RelatedEventBrief } from "@/lib/api";

/* ============================================
   Source Card Component
   ============================================ */

function SourceCard({ source, index }: { source: SourceResponse; index: number }) {
  const typeColors: Record<string, { bg: string; text: string; icon: string }> = {
    quran: { bg: "bg-emerald-50", text: "text-emerald-700", icon: "📖" },
    hadith_sahih: { bg: "bg-blue-50", text: "text-blue-700", icon: "📜" },
    hadith_hasan: { bg: "bg-cyan-50", text: "text-cyan-700", icon: "📜" },
    hadith_daif: { bg: "bg-stone-50", text: "text-stone-700", icon: "📜" },
    scholarly_consensus: { bg: "bg-amber-50", text: "text-amber-700", icon: "🎓" },
    historical: { bg: "bg-orange-50", text: "text-orange-700", icon: "🏛️" },
  };
  const style = typeColors[source.sourceType] || typeColors.historical;

  return (
    <AnimatedCard delay={index * 0.08}>
      <div className={`${style.bg} rounded-xl p-5 border border-transparent hover:border-[var(--color-gold)]/20 transition-all`}>
        <div className="flex items-start gap-3">
          <span className="text-lg">{style.icon}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge value={source.sourceType} />
              {source.grading && <Badge value={source.grading.toLowerCase()} />}
            </div>
            <p className="text-xs font-semibold text-[var(--color-ink-muted)] mb-2">{source.reference}</p>
            <p className="text-sm text-[var(--color-ink)] leading-relaxed italic">"{source.text}"</p>
            {source.textArabic && (
              <p className="font-arabic text-base text-[var(--color-gold-dim)]/80 mt-2 leading-loose">
                {source.textArabic}
              </p>
            )}
            {source.narrator && (
              <p className="text-xs text-[var(--color-ink-muted)] mt-2">
                Narrated by: {source.narrator}
              </p>
            )}
          </div>
        </div>
      </div>
    </AnimatedCard>
  );
}

/* ============================================
   Seerah Detail Page
   ============================================ */

export default function SeerahDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isBookmarked, toggle } = useBookmarks();
  const toast = useToast();

  // API hook
  const { data: event, isLoading, isError, error, refetch } = useSeerahEvent(id);

  const bookmarked = id ? isBookmarked(`seerah-${id}`) : false;

  useSEO(event ? event.title : "Seerah Event", snippet(event?.description), { type: "article", noindex: !isLoading && !event });

  usePageView("seerah", { id: event?.id, type: "event", title: event?.title });

  const handleBookmark = () => {
    if (!event || !id) return;
    const added = toggle({
      id: `seerah-${id}`,
      type: "seerah",
      title: event.title,
      subtitle: `${event.era} · ${event.dateGregorian || ""}`,
      route: `/seerah/${id}`,
    });
    toast.success(added ? "Bookmarked!" : "Bookmark removed");
  };

  // Loading state
  if (isLoading) {
    return (
      <main className="pt-16">
        <div className="bg-[var(--color-midnight)] py-16 md:py-24" />
        <Section><SkeletonDetailPage /></Section>
      </main>
    );
  }

  // Error state (a 404 falls through to "not found" below)
  if (isError && !isNotFound(error)) {
    return (
      <main className="pt-16">
        <Section>
          <div className="max-w-2xl mx-auto text-center py-20">
            <AlertCircle className="w-16 h-16 text-red-300 mx-auto mb-4" />
            <h2 className="font-[var(--font-display)] text-2xl font-bold text-[var(--color-midnight)] mb-2">
              Failed to Load Event
            </h2>
            <p className="text-[var(--color-ink-muted)] mb-6">{errorMessage(error)}</p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={() => void refetch()} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--color-cream-dark)] text-sm font-medium hover:bg-[var(--color-cream)] transition-colors">
                <RefreshCw className="w-3.5 h-3.5" /> Retry
              </button>
              <Link to="/seerah" className="btn-gold inline-flex items-center gap-2 text-sm">
                <ArrowLeft className="w-4 h-4" /> Back to Seerah
              </Link>
            </div>
          </div>
        </Section>
      </main>
    );
  }

  // Not found
  if (!event) {
    return (
      <main className="pt-16">
        <Section>
          <div className="max-w-2xl mx-auto text-center py-20">
            <BookOpen className="w-16 h-16 text-[var(--color-cream-dark)] mx-auto mb-4" />
            <h2 className="font-[var(--font-display)] text-2xl font-bold text-[var(--color-midnight)] mb-2">Event Not Found</h2>
            <p className="text-[var(--color-ink-muted)] mb-6">This event doesn't exist or has been removed.</p>
            <Link to="/seerah" className="btn-gold inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Back to Seerah
            </Link>
          </div>
        </Section>
      </main>
    );
  }

  return (
    <main className="pt-16">
      <ReadingProgress />
      <JsonLd data={articleJsonLd({ title: event.title, description: snippet(event.description), path: `/seerah/${event.id}`, datePublished: event.createdAt, section: "Seerah" })} />
      {/* Hero */}
      <div className="bg-[var(--color-midnight)] py-16 md:py-24 pattern-islamic relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-midnight)] via-transparent to-[var(--color-midnight)]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {/* Back + Bookmark */}
            <div className="flex items-center justify-between mb-6">
              <button onClick={() => navigate("/seerah")} className="inline-flex items-center gap-2 text-[var(--color-cream)]/50 hover:text-[var(--color-cream)] text-sm transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back to Seerah Explorer
              </button>
              <button onClick={handleBookmark}
                className={`p-2 rounded-lg transition-colors ${bookmarked ? "text-[var(--color-gold)] bg-[var(--color-gold)]/10" : "text-[var(--color-cream)]/40 hover:text-[var(--color-gold)] hover:bg-white/5"}`}
                title={bookmarked ? "Remove bookmark" : "Bookmark this event"}>
                <Bookmark className={`w-5 h-5 ${bookmarked ? "fill-current" : ""}`} />
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Badge value={event.era} />
              <Badge value={event.category} />
              {event.dateGregorian && (
                <span className="text-xs text-[var(--color-cream)]/40 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {event.dateGregorian}
                </span>
              )}
              {event.dateHijri && (
                <span className="text-xs text-[var(--color-gold)]/60 flex items-center gap-1">
                  <Star className="w-3 h-3" /> {event.dateHijri}
                </span>
              )}
            </div>

            <h1 className="font-[var(--font-display)] text-3xl md:text-5xl font-bold text-[var(--color-cream)] leading-tight">
              {event.title}
            </h1>

            {event.titleArabic && (
              <p className="font-arabic text-2xl md:text-3xl text-[var(--color-gold)]/60 mt-3">{event.titleArabic}</p>
            )}

            {event.location && (
              <p className="text-[var(--color-cream)]/40 text-sm mt-4 flex items-center gap-1.5">
                <MapPin className="w-4 h-4" /> {event.location}
              </p>
            )}
          </motion.div>
        </div>
      </div>

      <Section>
        <div className="max-w-4xl mx-auto">
          <Breadcrumbs crumbs={[{ label: "Seerah", to: "/seerah" }, { label: event.title }]} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              <AnimatedCard>
                <div className="prose prose-sm max-w-none">
                  {event.description.split("\n\n").map((para: string, i: number) => (
                    <p key={i} className="text-[var(--color-ink)] leading-relaxed mb-4">{para}</p>
                  ))}
                </div>
              </AnimatedCard>

              {/* Significance */}
              {event.significance && (
                <AnimatedCard delay={0.1}>
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-100">
                    <h3 className="font-[var(--font-display)] text-lg font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                      <Star className="w-5 h-5" /> Significance
                    </h3>
                    <p className="text-sm text-emerald-900/80 leading-relaxed">{event.significance}</p>
                  </div>
                </AnimatedCard>
              )}

              {/* Sources */}
              {event.sources.length > 0 && (
                <div>
                  <h3 className="font-[var(--font-display)] text-xl font-semibold text-[var(--color-midnight)] mb-4 flex items-center gap-2">
                    <Quote className="w-5 h-5 text-[var(--color-gold)]" /> Authenticated Sources
                  </h3>
                  <div className="space-y-3">
                    {event.sources.map((source: SourceResponse, i: number) => (
                      <SourceCard key={source.id} source={source} index={i} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Related Events */}
              {event.relatedEvents?.length > 0 && (
                <AnimatedCard delay={0.2}>
                  <div className="card-elevated p-5">
                    <h4 className="font-[var(--font-display)] text-base font-semibold text-[var(--color-midnight)] mb-4 flex items-center gap-2">
                      <Link2 className="w-4 h-4 text-[var(--color-gold)]" /> Related Events
                    </h4>
                    <div className="space-y-2">
                      {event.relatedEvents.map((rel: RelatedEventBrief) => (
                        <Link key={rel.id} to={`/seerah/${rel.id}`}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-[var(--color-cream)] transition-colors group">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[var(--color-midnight)] group-hover:text-emerald-700 transition-colors truncate">
                              {rel.title}
                            </p>
                            <Badge value={rel.era} />
                          </div>
                          <ChevronRight className="w-4 h-4 text-[var(--color-ink-muted)] group-hover:text-emerald-600 shrink-0" />
                        </Link>
                      ))}
                    </div>
                  </div>
                </AnimatedCard>
              )}

              {/* Quick Facts */}
              <AnimatedCard delay={0.4}>
                <div className="card-elevated p-5">
                  <h4 className="font-[var(--font-display)] text-base font-semibold text-[var(--color-midnight)] mb-4">Quick Facts</h4>
                  <dl className="space-y-3 text-sm">
                    {event.dateHijri && (
                      <div>
                        <dt className="text-[10px] text-[var(--color-ink-muted)] uppercase tracking-wider">Islamic Date</dt>
                        <dd className="text-[var(--color-midnight)] font-medium">{event.dateHijri}</dd>
                      </div>
                    )}
                    {event.dateGregorian && (
                      <div>
                        <dt className="text-[10px] text-[var(--color-ink-muted)] uppercase tracking-wider">Gregorian Date</dt>
                        <dd className="text-[var(--color-midnight)] font-medium">{event.dateGregorian}</dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-[10px] text-[var(--color-ink-muted)] uppercase tracking-wider">Location</dt>
                      <dd className="text-[var(--color-midnight)] font-medium">{event.location}</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] text-[var(--color-ink-muted)] uppercase tracking-wider">Sources</dt>
                      <dd className="text-[var(--color-midnight)] font-medium">{event.sources.length} references</dd>
                    </div>
                  </dl>
                </div>
              </AnimatedCard>
            </div>
          </div>
        </div>
      </Section>
    </main>
  );
}
